/**
 * Integrations section: provider cards (Sol-Ark, EG4, etc.) with status, update credentials, test, remove.
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Plug, RefreshCw, Trash2, KeyRound } from 'lucide-react';
import { ProviderBadge } from '@/components/ui/ProviderBadge';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const PROVIDER_LABEL: Record<string, string> = {
  solark: 'Sol-Ark',
  eg4: 'EG4',
  solaredge: 'SolarEdge',
  franklinwh: 'FranklinWH',
  enphase: 'Enphase',
  tesla: 'Tesla',
  manual: 'Manual',
  victron: 'Victron',
};

interface SystemRow {
  id: string;
  provider_type: string | null;
  last_poll_at: string | null;
  last_poll_status: string | null;
  consecutive_failures: number | null;
  backoff_until: string | null;
  property_id: string;
}

interface ProviderGroup {
  provider_type: string;
  systems: SystemRow[];
  lastPollAt: string | null;
  maxFailures: number;
}

function getStatus(group: ProviderGroup): { label: string; className: string } {
  const hasSuccess = group.systems.some((s) => s.last_poll_status === 'success' && (s.consecutive_failures ?? 0) === 0);
  const maxF = group.maxFailures;
  if (hasSuccess && maxF === 0) return { label: 'Connected', className: 'bg-wt-green-primary/20 text-wt-green-primary border-wt-green-primary/30' };
  if (maxF >= 10) return { label: 'Expired', className: 'bg-wt-red-primary/20 text-wt-red-primary border-wt-red-primary/30' };
  if (maxF > 0) return { label: `Failing (${maxF} failures)`, className: 'bg-wt-amber-primary/20 text-wt-amber-primary border-wt-amber-primary/30' };
  return { label: 'Unknown', className: 'bg-muted text-muted-foreground' };
}

export function IntegrationsSection() {
  const { properties, refreshData } = useApp();
  const [groups, setGroups] = useState<ProviderGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [updateModal, setUpdateModal] = useState<{ provider: string; systemIds: string[] } | null>(null);
  const [removeModal, setRemoveModal] = useState<{ provider: string; systemIds: string[] } | null>(null);
  const [testSystemId, setTestSystemId] = useState<string | null>(null);
  const [updateEmail, setUpdateEmail] = useState('');
  const [updatePassword, setUpdatePassword] = useState('');
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const propertyIds = properties.map((p) => p.id);

  useEffect(() => {
    if (propertyIds.length === 0) {
      setGroups([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('energy_systems')
        .select('id, provider_type, last_poll_at, last_poll_status, consecutive_failures, backoff_until, property_id')
        .in('property_id', propertyIds)
        .not('provider_type', 'is', null);

      if (cancelled) return;
      if (error) {
        toast.error('Failed to load integrations');
        setLoading(false);
        return;
      }
      const rows = (data ?? []) as SystemRow[];
      const byProvider = new Map<string, SystemRow[]>();
      for (const r of rows) {
        const p = (r.provider_type ?? 'manual').toLowerCase();
        if (!byProvider.has(p)) byProvider.set(p, []);
        byProvider.get(p)!.push(r);
      }
      const result: ProviderGroup[] = [];
      for (const [provider_type, systems] of byProvider) {
        const lastPollAt = systems.reduce<string | null>((acc, s) => {
          if (!s.last_poll_at) return acc;
          if (!acc) return s.last_poll_at;
          return s.last_poll_at > acc ? s.last_poll_at : acc;
        }, null);
        const maxFailures = Math.max(...systems.map((s) => s.consecutive_failures ?? 0), 0);
        result.push({ provider_type, systems, lastPollAt, maxFailures });
      }
      setGroups(result);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [propertyIds.join(',')]);

  // Deep-link: /app/settings?tab=integrations&provider=<x> auto-opens the reconnect
  // modal for that provider (from a credential-failure notification / Issues CTA).
  useEffect(() => {
    if (loading) return;
    const provider = searchParams.get('provider');
    if (!provider) return;
    const group = groups.find((g) => g.provider_type === provider.toLowerCase());
    if (group) {
      setUpdateModal({ provider: group.provider_type, systemIds: group.systems.map((s) => s.id) });
    }
    // Consume the param so the modal doesn't reopen on close.
    searchParams.delete('provider');
    setSearchParams(searchParams, { replace: true });
  }, [loading, groups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateCredentials = async () => {
    if (!updateModal || !updateEmail.trim() || !updatePassword.trim()) return;
    setUpdateSubmitting(true);
    try {
      const discoverFnMap: Record<string, string> = {
        eg4: 'eg4-discover',
        solark: 'solark-discover',
        franklinwh: 'franklinwh-discover',
        solaredge: 'solaredge-discover',
      };
      const discoverFn = discoverFnMap[updateModal.provider] ?? 'solark-discover';
      const body = updateModal.provider === 'solaredge'
        ? { apiKey: updatePassword.trim() }
        : { email: updateEmail.trim(), password: updatePassword.trim(), username: updateEmail.trim() };
      const { data, error } = await supabase.functions.invoke(discoverFn, { body });
      if (error) throw error;
      if (!data?.success) throw new Error((data as { error?: string })?.error ?? 'Validation failed');

      const credentials = updateModal.provider === 'solaredge'
        ? { apiKey: updatePassword.trim() }
        : { username: updateEmail.trim(), password: updatePassword.trim() };

      for (const systemId of updateModal.systemIds) {
        const { error: updateErr } = await supabase.functions.invoke('systems-update-credentials', {
          body: { systemId, credentials },
        });
        if (updateErr) throw updateErr;
      }
      await supabase
        .from('energy_systems')
        .update({ consecutive_failures: 0, backoff_until: null })
        .in('id', updateModal.systemIds);
      toast.success('Credentials updated');
      setUpdateModal(null);
      setUpdateEmail('');
      setUpdatePassword('');
      refreshData();
      setLoading(true);
      setTimeout(() => setLoading(false), 500);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update credentials');
    } finally {
      setUpdateSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!removeModal) return;
    setRemoveSubmitting(true);
    try {
      await supabase
        .from('energy_systems')
        .update({ credentials_enc: null, is_active: false })
        .in('id', removeModal.systemIds);
      toast.success('Credentials removed');
      setRemoveModal(null);
      refreshData();
      setLoading(true);
      setTimeout(() => setLoading(false), 500);
    } catch (err) {
      toast.error('Failed to remove');
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const handleTestConnection = async (systemId: string) => {
    setTestSystemId(systemId);
    try {
      const { data, error } = await supabase.functions.invoke('systems-test-connection', {
        body: { systemId },
      });
      if (error) throw error;
      if (data?.success) {
        toast.success(data?.message ?? 'Connection successful');
      } else {
        toast.error(data?.error ?? 'Connection failed');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTestSystemId(null);
    }
  };

  if (loading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-wt-text-secondary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-wt-text-secondary">
        Connected inverter and battery APIs. Update credentials if login fails or you rotate passwords.
      </p>
      {groups.length === 0 ? (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-12 text-center text-wt-text-secondary">
            No integrations yet. Add systems via onboarding or property setup.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const status = getStatus(group);
            const firstSystem = group.systems[0];
            return (
              <Card key={group.provider_type} className="bg-wt-bg-secondary border-border">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Plug className="w-4 h-4" />
                      <ProviderBadge provider={group.provider_type} />
                    </CardTitle>
                    <Badge variant="outline" className={status.className}>{status.label}</Badge>
                  </div>
                  <CardDescription>
                    {group.systems.length} system{group.systems.length !== 1 ? 's' : ''}
                    {group.lastPollAt && ` · Last poll ${formatDistanceToNow(new Date(group.lastPollAt), { addSuffix: true })}`}
                    {group.maxFailures > 0 && (
                      <> · {group.maxFailures} consecutive failure{group.maxFailures !== 1 ? 's' : ''}</>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUpdateModal({ provider: group.provider_type, systemIds: group.systems.map((s) => s.id) })}
                  >
                    <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                    Update Credentials
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestConnection(firstSystem.id)}
                    disabled={testSystemId !== null}
                  >
                    {testSystemId === firstSystem.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Test Connection
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setRemoveModal({ provider: group.provider_type, systemIds: group.systems.map((s) => s.id) })}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Remove
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!updateModal} onOpenChange={(open) => !open && setUpdateModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Credentials</DialogTitle>
            <DialogDescription>
              Enter new login for {updateModal && PROVIDER_LABEL[updateModal.provider]}. This will update all {updateModal?.systemIds.length} system(s) using this provider.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={updateEmail}
                onChange={(e) => setUpdateEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={updatePassword}
                onChange={(e) => setUpdatePassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateModal(null)}>Cancel</Button>
            <Button onClick={handleUpdateCredentials} disabled={updateSubmitting || !updateEmail.trim() || !updatePassword.trim()}>
              {updateSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeModal} onOpenChange={(open) => !open && setRemoveModal(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove credentials?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove stored credentials and deactivate polling for {removeModal?.systemIds.length} system(s).
              You can add credentials again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} disabled={removeSubmitting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {removeSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
